/** Actual source transferred from standalone-search f83fe2d1 via Registrar V2. Native lifecycle adaptation is documented in TRANSFER.json. */
/**
 * RecordSheetContent — a SheetContent that reserves the sidebar's width.
 *
 * Primary record surfaces (the question detail sheet, the workspace detail
 * sheet, the W3 trace, pending invitations) and every stacked sheet over them
 * open full-bleed beside the sidebar rather than as a narrow drawer. That was
 * eight files each importing `useSidebarWidth`, calling it, and interpolating
 * the same `calc(100vw - ...)` by hand, so a sidebar-width change was an
 * eight-file edit and the two primary record sheets had drifted onto different
 * full-screen breakpoints.
 *
 * This lives beside the primitive rather than inside it for two reasons:
 *   - `components/ui/sidebar.tsx` already imports `sheet.tsx`, so teaching the
 *     primitive about the sidebar would close an import cycle.
 *   - `useSidebar()` throws outside a `SidebarProvider`, and the admin app's
 *     nav drawer (`admin/components/AdminLayout.tsx`) is a SheetContent outside
 *     one. A hook in the primitive would crash it.
 *
 * It lives under `workbench/` rather than `components/ui/` for that same
 * reason: every consumer is a workbench sheet with a `SidebarProvider` above
 * it, but `components/ui/` is the directory the admin app reaches into for
 * primitives, and nothing there stopped an admin author from importing this
 * and hitting the same crash. `eslint.config.js` now forbids `src/admin/**`
 * from importing this path, so that guard is enforced, not just documented.
 *
 * See `.claude/docs/adr/0010-workbench-sheet-shell.md` (D2).
 */

import * as React from "react";

import { cn } from "@/lib/utils";
import { SheetContent } from "@/components/ui/sheet";
import { useSidebarWidth } from "@/components/ui/sidebar";

/**
 * `md` (768px) is the breakpoint, not `sm`, because it is the only one that
 * names something real: `MOBILE_BREAKPOINT` is 768, and that is exactly where
 * `useSidebarWidth()` starts returning "0px". The two spellings this replaced
 * (`max-sm` and `max-md`) computed to identical widths at every viewport —
 * below 768 the sidebar reserves nothing, so `calc(100vw - 0px)` and
 * `!w-screen` are the same number — so picking `md` changes no geometry.
 */
const RECORD_WIDTH_CLASS =
  "w-full min-w-0 max-w-full max-md:!w-screen md:max-w-none";

export function RecordSheetContent({
  className,
  style,
  ...props
}: React.ComponentProps<typeof SheetContent>) {
  const sidebarWidth = useSidebarWidth();

  return (
    <SheetContent
      className={cn(RECORD_WIDTH_CLASS, "bg-background", className)}
      style={{ width: `calc(100vw - ${sidebarWidth})`, ...style }}
      {...props}
    />
  );
}
